"use client";

import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import type { TFunction } from "i18next";
import {
  parseDate,
  formatDateISO,
  parseDateFromInput,
  formatDateInputAsYouType,
  DATE_FORMATS,
} from "@/lib/date";
import { toast } from "sonner";
import {
  isDateOutOfRange,
  isTodayDisabled,
} from "@/components/ui/utils/date-time-input-utils";

export type DateTimeInputVariant = "date" | "time";

export type UseDateTimeInputDateParams = {
  variant: DateTimeInputVariant;
  value: string | number | readonly string[] | undefined;
  max: string | undefined;
  min: string | undefined;
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined;
  t: TFunction;
  isError?: boolean;
  errorMessage?: string;
  helperText?: string;
};

export type UseDateTimeInputDateResult = {
  open: boolean;
  setOpen: (open: boolean) => void;
  displayValue: string;
  selectedDate: Date | undefined;
  maxDate: Date | undefined;
  minDate: Date | undefined;
  dateIsError: boolean;
  dateHelperOrError: boolean;
  dateHelperMessage: string | undefined;
  handleSelect: (date: Date | undefined) => void;
  handleDateInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateInputBlur: () => void;
  handleDateInputFocus: () => void;
  handleToday: () => void;
  handleClear: () => void;
  isTodayDisabled: boolean;
};

const STUB_RESULT: UseDateTimeInputDateResult = {
  open: false,
  setOpen: () => {},
  displayValue: "",
  selectedDate: undefined,
  maxDate: undefined,
  minDate: undefined,
  dateIsError: false,
  dateHelperOrError: false,
  dateHelperMessage: undefined,
  handleSelect: () => {},
  handleDateInputChange: () => {},
  handleDateInputBlur: () => {},
  handleDateInputFocus: () => {},
  handleToday: () => {},
  handleClear: () => {},
  isTodayDisabled: false,
};

export function useDateTimeInputDate({
  variant,
  value,
  max,
  min,
  onChange,
  t,
  isError = false,
  errorMessage,
  helperText,
}: UseDateTimeInputDateParams): UseDateTimeInputDateResult {
  const [open, setOpen] = useState(false);
  const [localInputText, setLocalInputText] = useState<string | undefined>(
    undefined
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const dateValue = variant === "date" && typeof value === "string" ? value : undefined;
  const selectedDate =
    variant === "date" ? parseDate(dateValue ?? "") ?? undefined : undefined;
  // Memoised so the bounds keep one identity across renders: they feed the calendar's
  // disabled-day matchers, which are costly to recompute for no reason.
  const maxDate = useMemo(
    () =>
      variant === "date"
        ? parseDate(typeof max === "string" ? max : "") ?? undefined
        : undefined,
    [variant, max]
  );
  const minDate = useMemo(
    () =>
      variant === "date"
        ? parseDate(typeof min === "string" ? min : "") ?? undefined
        : undefined,
    [variant, min]
  );

  // What the box shows when the user is not editing it - also the yardstick blur uses to
  // tell an edit apart from a tab-through.
  const committedText = selectedDate
    ? format(selectedDate, DATE_FORMATS.short)
    : "";

  const displayValue =
    variant === "date"
      ? localInputText !== undefined
        ? localInputText
        : committedText
      : "";

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (variant !== "date" || !date) return;
      const str = formatDateISO(date);
      setOpen(false);
      setLocalInputText(undefined);
      setValidationError(null);
      onChange?.(
        { target: { value: str } } as React.ChangeEvent<HTMLInputElement>
      );
    },
    [variant, onChange]
  );

  const handleDateInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (variant !== "date") return;
      const next = formatDateInputAsYouType(e.target.value, DATE_FORMATS.short);
      setLocalInputText(next);
      setValidationError(null);

      // Commit immediately when user has entered a full valid date so forms
      // can react (e.g. enable Save) without requiring blur.
      if (!next.trim()) {
        onChange?.(
          { target: { value: "" } } as React.ChangeEvent<HTMLInputElement>
        );
        return;
      }

      // A date outside min/max is left uncommitted while typing; blur reports it.
      const parsed = parseDateFromInput(next);
      if (parsed && !isDateOutOfRange(parsed, maxDate, minDate)) {
        onChange?.({
          target: { value: formatDateISO(parsed) },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    },
    [variant, onChange, maxDate, minDate]
  );

  const handleDateInputBlur = useCallback(() => {
    if (variant !== "date") return;
    const raw = (
      localInputText !== undefined ? localInputText : displayValue
    ).trim();

    // Focus seeds the box with the committed value; if the user never edited it there is
    // nothing to validate or re-commit. A pre-existing value outside min/max is the form
    // validator's concern, not this input's.
    if (raw === committedText) {
      setLocalInputText(undefined);
      setValidationError(null);
      return;
    }

    if (!raw) {
      onChange?.(
        { target: { value: "" } } as React.ChangeEvent<HTMLInputElement>
      );
      setLocalInputText(undefined);
      setValidationError(null);
      return;
    }
    const parsed = parseDateFromInput(raw);
    if (!parsed || isDateOutOfRange(parsed, maxDate, minDate)) {
      const message = t(parsed ? "dateOutOfRange" : "invalidDate");
      setValidationError(message);
      setLocalInputText(undefined);
      toast.error(message, { position: "top-right" });
      return;
    }
    onChange?.({
      target: { value: formatDateISO(parsed) },
    } as React.ChangeEvent<HTMLInputElement>);
    setLocalInputText(undefined);
    setValidationError(null);
  }, [
    variant,
    localInputText,
    displayValue,
    committedText,
    onChange,
    t,
    maxDate,
    minDate,
  ]);

  const handleDateInputFocus = useCallback(() => {
    if (variant !== "date") return;
    if (localInputText === undefined) {
      setLocalInputText(committedText);
    }
  }, [variant, localInputText, committedText]);

  const handleToday = useCallback(() => {
    if (variant !== "date") return;
    if (isTodayDisabled(maxDate, minDate)) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    handleSelect(today);
  }, [variant, maxDate, minDate, handleSelect]);

  const handleClear = useCallback(() => {
    if (variant !== "date") return;
    setOpen(false);
    setLocalInputText(undefined);
    setValidationError(null);
    onChange?.(
      { target: { value: "" } } as React.ChangeEvent<HTMLInputElement>
    );
  }, [variant, onChange]);

  if (variant !== "date") {
    return STUB_RESULT;
  }

  const dateIsError = isError || !!validationError;
  const dateHelperOrError = !!(
    helperText ||
    (isError && errorMessage) ||
    validationError
  );
  const dateHelperMessage =
    validationError ?? (isError ? errorMessage : undefined) ?? helperText;
  const todayDisabled = isTodayDisabled(maxDate, minDate);

  return {
    open,
    setOpen,
    displayValue,
    selectedDate,
    maxDate,
    minDate,
    dateIsError,
    dateHelperOrError,
    dateHelperMessage,
    handleSelect,
    handleDateInputChange,
    handleDateInputBlur,
    handleDateInputFocus,
    handleToday,
    handleClear,
    isTodayDisabled: todayDisabled,
  };
}
